import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Input,
  Label,
  Spinner,
  UncontrolledTooltip,
} from "reactstrap";
import {
  updateMaterialForOption,
  addLaminate,
  mappedColors,
  getLaminates,
  updateEdgeMaterial,
  createEdgeMaterial,
  deleteColor,
} from "./service";
import { CustomizationSpinnerContainer, Notification } from "../common/style";
import { useParams } from "react-router-dom";
import {
  ColorPicker,
  ColorIcon,
  ProjectButton,
  UploadButton,
} from "../project/style";
import { debounce } from "lodash";
import NoImage from "../assets/img/no_image.png";
import { Option } from "../product/style";
import Select from "react-select";
import ConfirmationModal from "./ConfirmationModal";
import { LAM_BRANDS, LAM_CATEGORIES, LAM_THICKNESSES } from "../constants";

const Colors = ({ loadRoom, currentRoom, access }) => {
  const [colors, setColors] = useState(null);
  const [count, setCount] = useState(null);
  const [edgeTapeMapping, setEdgeTapeMapping] = useState(null);
  const [edgeMaterials, setEdgeMaterials] = useState(null);
  const [searchedColors, setSearchedColors] = useState([]);
  const [showSearchColors, setShowSearchColors] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);
  const [colorSearchTerm, setColorSearchTerm] = useState("");
  const [colorSearchValue, setColorSearchValue] = useState("");
  const [colorsLoading, setColorsLoading] = useState(false);
  const [showUploadLaminate, setShowUploadLaminate] = useState(false);
  const [showUploadEdgeTape, setShowUploadEdgeTape] = useState(false);
  const uploadFileInputRef = useRef(null);
  const [laminateType, setLaminateType] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [disableUpload, setDisableUpload] = useState(true);
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState("laminates");
  const params = useParams();
  const projectId = params.projectId;
  const [edgeCode, setEdgeCode] = useState("");
  const [edgeName, setEdgeName] = useState("");
  const [edgeThickness, setEdgeThickness] = useState("");
  const [edgeBrand, setEdgeBrand] = useState("");
  const [edgeWidth, setEdgeWidth] = useState("");
  const [showRemoveColorConfirmation, setShowRemoveColorConfirmation] =
    useState(false);
  const [deleteOption, setDeleteOption] = useState("");
  const [formDetails, setFormDetails] = useState({
    brand: "",
    range: "",
    code: "",
    name: "",
    finish: "",
    thickness: "",
    category: "",
    grains: "no",
  });

  const toggleRemoveColorConfirmation = () =>
    setShowRemoveColorConfirmation(!showRemoveColorConfirmation);

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  const loadMappedColors = () => {
    setIsLoading(true);

    mappedColors({ projectId: projectId })
      .then((response) => {
        handleMappedColorsResponse(response);
      })
      .catch((error) => {
        setIsNotificationVisible(true);
        setNotification({ message: error.error, type: "error" });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    loadMappedColors();
  }, []);

  const handleLaminateChange = (option, laminate) => {
    setActionLoading(true);

    updateMaterialForOption({
      projectId: projectId,
      optionIdentifier: option,
      materialIdentifier: laminate,
      materialCategory: "color",
    })
      .then((response) => {
        clearInfo();
        setColors(response.colors);
        setCount(response.count);
        setEdgeTapeMapping(response.edge_tape_mappings);
        setEdgeMaterials(response.edge_materials);
        loadRoom(currentRoom.id);
      })
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotification({ message: error, type: "error" });
      })
      .finally(() => {
        setActionLoading(false);
      });
  };

  const handleSearch = (searchValue) => {
    setColorSearchValue(searchValue);
    debounceSearch(searchValue);
  };

  const debouncedSearch = (value) => setColorSearchTerm(value);

  const debounceSearch = useCallback(debounce(debouncedSearch, 500), []);

  useEffect(() => {
    if (colorSearchTerm) {
      setColorsLoading(true);

      getLaminates({ searchTerm: colorSearchTerm, manage: false })
        .then((response) => {
          setSearchedColors(response);
        })
        .catch((error) => {
          setIsNotificationVisible(true);
          setNotification({ message: error.error, type: "error" });
        })
        .finally(() => setColorsLoading(false));
    } else setSearchedColors([]);
  }, [colorSearchTerm]);

  const clearInfo = () => {
    setShowSearchColors(false);
    setColorSearchValue("");
    setColorSearchTerm("");
    setLaminateType(null);
    setActionType(null);
    setSelectedColor(null);
  };

  useEffect(() => {
    if (
      formDetails.brand &&
      formDetails.range.trim() &&
      formDetails.code.trim() &&
      formDetails.name.trim() &&
      formDetails.finish.trim() &&
      formDetails.thickness &&
      formDetails.category &&
      selectedFile
    )
      setDisableUpload(false);
    else setDisableUpload(true);
  }, [formDetails, selectedFile]);

  useEffect(() => {
    setFormDetails({
      brand: "",
      range: "",
      code: "",
      name: "",
      finish: "",
      thickness: "",
      category: "",
      grains: "no",
    });
    if (uploadFileInputRef && uploadFileInputRef.current) removeFile();
    setErrorMessage(null);
  }, [showUploadLaminate]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      const fileType = file.type;
      const validImageTypes = ["image/jpeg", "image/png"];

      if (!validImageTypes.includes(fileType)) {
        setErrorMessage("Please select a valid image file.");
        setSelectedFile(null);
        event.target.value = "";
      } else {
        setErrorMessage("");
        setSelectedFile(file);
      }
    }
  };

  const removeFile = () => {
    uploadFileInputRef.current.value = "";
    setSelectedFile(null);
  };

  const uploadLaminate = (details, file) => {
    setActionLoading(true);

    addLaminate({ details, file })
      .then(({ message }) => {
        setShowUploadLaminate(false);
        setIsNotificationVisible(true);
        setNotification({ message: message, type: "success" });
      })
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotification({ message: error, type: "error" });
      })
      .finally(() => setActionLoading(false));
  };

  const handleEdgeMaterialChange = ({ edgeCategory, materialIdentifier }) => {
    setIsLoading(true);
    updateEdgeMaterial({
      projectId: projectId,
      edgeThickness: edgeCategory.thickness,
      edgeLaminateIdentifier: edgeCategory.laminate.identifier,
      edgePanelThickness: edgeCategory.panel_thickness,
      edgeMaterialIdentifier: materialIdentifier,
    })
      .then((response) => {
        handleMappedColorsResponse(response);
      })
      .catch(({ error }) => {
        setNotification({ message: error, type: "error" });
        setIsNotificationVisible(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleMappedColorsResponse = (response) => {
    setColors(response.colors);
    setCount(response.count);
    setEdgeTapeMapping(response.edge_tape_mappings);
    setEdgeMaterials(response.edge_materials);
  };

  const getEdgeMaterialOption = (materialIdentifier, index) => {
    if (materialIdentifier === null) {
      return null;
    }
    const material = edgeMaterials.find(
      (material) => material.identifier === materialIdentifier
    );
    if (!material) {
      return null;
    }
    return {
      name: material.name,
      label: material.name,
      value: material.identifier,
    };
  };

  const metaDataFooter = (data) => ({
    alignItems: "left",
    display: "flex",
    paddingLeft: "10px",
    fontSize: "14px",
    paddingBottom: "8px",
    borderBottomStyle: "solid",
    borderBottomColor: "#ddd",
    borderBottomWidth: "1px",
    cursor: "pointer",
    ":after": {
      content: `"${data.width}\\00a0mm/${data.thickness}\\00a0mm"`,
      borderRadius: 10,
      display: "flex",
      marginRight: 8,
      height: 10,
      width: 10,
      color: "#6c757d",
      fontSize: "12px",
    },
  });

  const colourStyles = {
    control: (styles) => ({ ...styles, backgroundColor: "white" }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      return {
        display: "flex",
        alignContent: "left",
        flexGrow: 1,
        flexDirection: "column",
        ...metaDataFooter(data),
      };
    },
  };

  const createNewEdgeTape = (brand, name, code, thickness, width) => {
    setActionLoading(true);
    createEdgeMaterial({
      brand: brand,
      name: name,
      code: code,
      thickness: thickness,
      width: width,
    })
      .then((response) => {
        setShowUploadEdgeTape(false);
        setEdgeMaterials([...edgeMaterials, response]);
      })
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotification({ message: error, type: "error" });
      })
      .finally(() => setActionLoading(false));
  };

  const thicknessOptions = [
    { name: "0.5 mm", label: "0.5 mm", value: "0.5" },
    { name: "0.8 mm", label: "0.8 mm", value: "0.8" },
    { name: "1.3 mm", label: "1.3 mm", value: "1.3" },
    { name: "1.5 mm", label: "1.5 mm", value: "1.5" },
    { name: "2 mm", label: "2 mm", value: "2" },
  ];

  const widthOptions = [
    { name: "22 mm", label: "22 mm", value: "22" },
    { name: "25 mm", label: "25 mm", value: "25" },
    { name: "30 mm", label: "30 mm", value: "30" },
    { name: "40 mm", label: "40 mm", value: "40" },
  ];

  const brandOptions = [
    { name: "E3", label: "E3", value: "E3" },
    { name: "RD", label: "RD", value: "RD" },
    { name: "Rehau", label: "Rehau", value: "Rehau" },
    { name: "SmartEdge", label: "SmartEdge", value: "SmartEdge" },
    { name: "Star", label: "Star", value: "Star" },
    { name: "MasterEdge", label: "MasterEdge", value: "MasterEdge" },
    { name: "CrownEdge", label: "CrownEdge", value: "CrownEdge" },
  ];

  const deleteColorEntry = (optionIdentifier) => {
    setIsLoading(true);

    deleteColor({ projectId, optionIdentifier })
      .then((response) => handleMappedColorsResponse(response))
      .catch((error) => {
        setIsNotificationVisible(true);
        setNotification({ message: error.error, type: "error" });
      })
      .finally(() => {
        toggleRemoveColorConfirmation();
        setIsLoading(false);
      });
  };

  const categories = LAM_CATEGORIES.map((e) => {
    if (e === "lam_1") return { label: "Less than ₹350", value: e };
    else if (e === "lam_4500_plus")
      return { label: "More than ₹4500", value: e };
    else {
      const range = e.split("_").slice(1);

      return { label: `₹${range[0]} to ₹${range[1]}`, value: e };
    }
  });

  const brands = LAM_BRANDS.map((e) => ({
    label: e,
    value: e,
  }));

  const thicknesses = LAM_THICKNESSES.map((e) => ({
    label: e,
    value: e,
  }));

  return (
    <>
      {notification.type && (
        <Notification
          color={notification.type === "error" ? "danger" : "success"}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notification.message}
        </Notification>
      )}

      <div className={`fh overflow-scroll ${isLoading ? "blurred" : ""}`}>
        {isLoading ? (
          <CustomizationSpinnerContainer>
            <Spinner />
          </CustomizationSpinnerContainer>
        ) : (
          <>
            {actionLoading && (
              <CustomizationSpinnerContainer top="200px">
                <Spinner />
              </CustomizationSpinnerContainer>
            )}
            {access.show_upload_laminate && showUploadLaminate ? (
              <div
                className={`${actionLoading ? "blurred" : ""}`}
                style={{ marginBottom: "10rem" }}
              >
                <div className="d-flex column-gap-3 align-items-center px-3 py-2">
                  <i
                    onClick={() => setShowUploadLaminate(false)}
                    className="fa-solid fa-arrow-left fs-5 pointer text-secondary"
                  />
                  <p className="fw-bold fs-6 m-0">Upload Laminate</p>
                </div>
                <div>
                  <div className="px-3 py-2">
                    <p className="mb-1 fw-bold">Brand</p>
                    <Select
                      options={brands}
                      value={brands.find((e) => e.value == formDetails.brand)}
                      onChange={(e) =>
                        setFormDetails({ ...formDetails, brand: e.value })
                      }
                    />
                  </div>
                  <div className="px-3 py-2">
                    <p className="mb-1 fw-bold">Color code</p>
                    <Input
                      type="text"
                      className="py-2 rounded-1"
                      value={formDetails.code}
                      onChange={(e) =>
                        setFormDetails({
                          ...formDetails,
                          code: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="px-3 py-2">
                    <p className="mb-1 fw-bold">Color name</p>
                    <Input
                      type="text"
                      className="py-2 rounded-1"
                      value={formDetails.name}
                      onChange={(e) =>
                        setFormDetails({
                          ...formDetails,
                          name: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="px-3 py-2">
                    <p className="mb-1 fw-bold">Collection</p>
                    <Input
                      type="text"
                      className="py-2 rounded-1"
                      value={formDetails.range}
                      onChange={(e) =>
                        setFormDetails({
                          ...formDetails,
                          range: e.target.value,
                        })
                      }
                    />
                    <div className="mt-2 text-secondary">
                      Ex: Liner, Solids.
                    </div>
                  </div>
                  <div className="px-3 py-2">
                    <p className="mb-1 fw-bold">Finish</p>
                    <Input
                      type="text"
                      className="py-2 rounded-1"
                      value={formDetails.finish}
                      onChange={(e) =>
                        setFormDetails({
                          ...formDetails,
                          finish: e.target.value,
                        })
                      }
                    />
                    <div className="mt-2 text-secondary">
                      Ex: SF, HGL, VNR, etc.
                    </div>
                  </div>
                  <div className="px-3 py-2">
                    <p className="mb-1 fw-bold">Thickness</p>
                    <Select
                      options={thicknesses}
                      value={thicknesses.find(
                        (e) => e.value == formDetails.thickness
                      )}
                      onChange={(e) =>
                        setFormDetails({ ...formDetails, thickness: e.value })
                      }
                    />
                  </div>
                  <div className="px-3 py-2">
                    <p className="mb-1 fw-bold">MRP range</p>
                    <Select
                      options={categories}
                      value={categories.find(
                        (e) => e.value == formDetails.category
                      )}
                      onChange={(e) =>
                        setFormDetails({ ...formDetails, category: e.value })
                      }
                    />
                    <div className="mt-2 text-secondary">
                      If price incl. tax is ₹1400, select 1250 - 1500.
                    </div>
                  </div>
                  <div className="px-3 py-2">
                    <div className="mb-3">
                      <p className="mb-1 fw-bold">Image</p>
                      <div className="d-flex align-items-center">
                        <Label
                          className="border border-secondary rounded-2 m-0"
                          htmlFor="file-upload"
                          style={{ padding: "6px 14px" }}
                        >
                          Select File
                        </Label>
                        <Input
                          className="d-none"
                          id="file-upload"
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          innerRef={uploadFileInputRef}
                        />

                        {selectedFile && (
                          <div
                            className="d-flex align-items-center ms-2 column-gap-1"
                            style={{ fontSize: "14px" }}
                          >
                            <span>{selectedFile.name}</span>
                            <i
                              className="fa-regular fa-circle-xmark pointer danger-on-hover"
                              onClick={() => removeFile()}
                            />
                          </div>
                        )}

                        {errorMessage && (
                          <span className="ms-2 text-danger">
                            {errorMessage}
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="mb-1 fw-bold">Has grains or patterns?</p>
                      <div className="btn-group">
                        <Option
                          onClick={() =>
                            setFormDetails({ ...formDetails, grains: "no" })
                          }
                          className={`default-option ${
                            formDetails.grains === "no" && "selected-option"
                          }`}
                        >
                          No
                        </Option>
                        <Option
                          onClick={() =>
                            setFormDetails({ ...formDetails, grains: "yes" })
                          }
                          className={`default-option ${
                            formDetails.grains === "yes" && "selected-option"
                          }`}
                        >
                          Yes
                        </Option>
                      </div>
                      <div className="mt-2 text-secondary">
                        Note: If set to <b>No</b>, this laminate may be rotated
                        during fabrication.
                      </div>
                    </div>
                  </div>
                  <div className="d-flex px-3 py-2 column-gap-3">
                    <ProjectButton
                      onClick={() => uploadLaminate(formDetails, selectedFile)}
                      disabled={disableUpload || actionLoading}
                      className="rounded-1"
                    >
                      Upload
                    </ProjectButton>
                    <Button
                      onClick={() => setShowUploadLaminate(false)}
                      className="rounded-1"
                      outline
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            ) : showSearchColors ? (
              <>
                <div className="d-flex justify-content-between align-items-center border-bottom px-3 py-2">
                  <div className="d-flex column-gap-3 align-items-center">
                    <i
                      onClick={() => clearInfo()}
                      className="fa-solid fa-arrow-left fs-5 pointer"
                    />
                    {actionType === "add" ? (
                      <p className="fw-bold fs-6 m-0">Add Color</p>
                    ) : (
                      <div className="d-flex column-gap-2 align-items-center">
                        <ColorPicker className="m-0" height="40px" width="40px">
                          <img
                            className="h-100 w-100"
                            src={selectedColor.image}
                          />
                        </ColorPicker>
                        <p className="m-0">{selectedColor.short_name}</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className={`${actionLoading ? "blurred" : ""}`}>
                  <div className="p-3">
                    <Input
                      type="search"
                      value={colorSearchValue}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="rounded-1 px-3 py-2"
                      placeholder="Search by laminate code, name or brand"
                      autoFocus
                    />
                  </div>
                  {!colorSearchValue && (
                    <Card className="d-flex justify-content-center align-items-center empty px-3 m-3">
                      Type to search laminates
                    </Card>
                  )}
                  <div
                    className={`${colorsLoading ? "blurred" : ""} px-3`}
                    style={{ paddingBottom: "70px" }}
                  >
                    {searchedColors.length > 0 ? (
                      <div className="d-flex flex-wrap column-gap-4 row-gap-2">
                        {searchedColors.map((color, index) => (
                          <>
                            <div
                              id={"color_" + index}
                              className={`d-flex flex-column align-items-center ${
                                selectedColor &&
                                selectedColor.rotatable &&
                                !color.rotatable
                                  ? "not-allowed-cursor opacity-25"
                                  : "pointer"
                              }`}
                              onClick={() => {
                                if (
                                  selectedColor &&
                                  selectedColor.rotatable &&
                                  !color.rotatable
                                )
                                  return;

                                handleLaminateChange(
                                  selectedColor
                                    ? selectedColor.prop_identifier
                                    : laminateType,
                                  color.identifier
                                );
                              }}
                              key={index}
                            >
                              <ColorPicker
                                className={`${
                                  access.change_color && "disabled"
                                }`}
                              >
                                <img
                                  className="h-100 w-100"
                                  src={color.image ? color.image : NoImage}
                                />
                              </ColorPicker>
                              <p className="m-0">{color.short_name}</p>
                            </div>
                            <UncontrolledTooltip
                              target={"color_" + index}
                              placement="bottom"
                            >
                              {selectedColor &&
                              selectedColor.rotatable &&
                              !color.rotatable
                                ? "Cannot change color from no-grain to grain"
                                : color.name}
                            </UncontrolledTooltip>
                          </>
                        ))}
                      </div>
                    ) : (
                      colorSearchTerm.length > 0 && (
                        <div className="mt-2 text-center fw-bold">
                          No Laminates found
                        </div>
                      )
                    )}
                  </div>
                  <div
                    className="position-fixed bottom-0 d-flex align-items-center px-2 py-3 border-top border-secondary-subtle"
                    style={{
                      background: "#dddddd",
                      right: 0,
                      width: "300px",
                    }}
                  >
                    <p className="mb-0 me-1">Could not find the laminate?</p>
                    <UploadButton onClick={() => setShowUploadLaminate(true)}>
                      <i className="fa-solid fa-circle-plus me-1"></i>
                      Upload Laminate
                    </UploadButton>
                  </div>
                </div>
              </>
            ) : showUploadEdgeTape ? (
              <>
                <div className={`${actionLoading ? "blurred" : ""}`}>
                  <div className="d-flex column-gap-3 align-items-center px-2 py-3">
                    <i
                      onClick={() => setShowUploadEdgeTape(false)}
                      className="fa-solid fa-arrow-left fs-5 pointer text-secondary"
                    />
                    <p className="fw-bold fs-6 m-0">Add Edge Tape</p>
                  </div>
                  <div>
                    <div className="px-3 py-2">
                      <p className="mb-1 fw-bold">Brand</p>
                      <Select
                        value={edgeBrand}
                        className="rounded-1 w-50"
                        options={brandOptions}
                        onChange={(e) => {
                          setEdgeBrand(e);
                        }}
                        placeholder={"Select brand"}
                      />
                    </div>
                    <div className="px-3 py-2">
                      <p className="mb-1 fw-bold">Code</p>
                      <Input
                        value={edgeCode}
                        onChange={(ev) => setEdgeCode(ev.target.value)}
                        type="text"
                        className="py-2 rounded-1 w-50"
                      />
                    </div>
                    <div className="px-3 py-2">
                      <p className="mb-1 fw-bold">Name</p>
                      <Input
                        value={edgeName}
                        onChange={(ev) => setEdgeName(ev.target.value)}
                        type="text"
                        className="py-2 rounded-1"
                      />
                    </div>
                    <div className="px-3 py-2">
                      <p className="mb-1 fw-bold">Width (mm)</p>
                      <Select
                        value={edgeWidth}
                        className="rounded-1 w-50"
                        options={widthOptions}
                        onChange={(e) => {
                          setEdgeWidth(e);
                        }}
                        placeholder={"Select width"}
                      />
                    </div>
                    <div className="px-3 py-2">
                      <p className="mb-1 fw-bold">Thickness</p>
                      <Select
                        value={edgeThickness}
                        className="rounded-1 w-50"
                        options={thicknessOptions}
                        onChange={(e) => {
                          setEdgeThickness(e);
                        }}
                        placeholder={"Select thickness"}
                      />
                    </div>

                    <div className="d-flex px-3 py-2 column-gap-3">
                      <ProjectButton
                        onClick={() =>
                          createNewEdgeTape(
                            edgeBrand.value,
                            edgeName,
                            edgeCode,
                            edgeThickness.value,
                            edgeWidth.value
                          )
                        }
                        className="rounded-1"
                        disabled={
                          !(edgeBrand && edgeName && edgeCode && edgeThickness)
                        }
                      >
                        Add Edge Tape
                      </ProjectButton>
                      <Button
                        onClick={() => setShowUploadEdgeTape(false)}
                        className="rounded-1"
                        outline
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {access.view_edges && (
                  <div className={"flex-row flex border-bottom ps-2 pt-2"}>
                    <div
                      className={`tab mx-2 border-3 align-self-center ${
                        selectedTab === "laminates" ? "active" : ""
                      }`}
                      onClick={() => setSelectedTab("laminates")}
                    >
                      Laminates
                    </div>
                    <div
                      className={`tab mx-2 border-3 align-self-center ${
                        selectedTab === "edges" ? "active" : ""
                      }`}
                      onClick={() => setSelectedTab("edges")}
                    >
                      Edges
                    </div>
                  </div>
                )}

                {selectedTab === "laminates" && (
                  <>
                    <div className="p-3">
                      <p className="mb-2 fw-bold fs-14px">Internal</p>
                      <div className="d-flex flex-wrap column-gap-4 row-gap-2">
                        {colors &&
                          colors.internal.map((color, index) => (
                            <div
                              className="d-flex flex-column align-items-center"
                              key={index}
                            >
                              <>
                                <ColorPicker id={"internal_" + index}>
                                  {color.unused_color && (
                                    <div className="unused_color_text">
                                      Unused
                                    </div>
                                  )}
                                  <img
                                    className="h-100 w-100"
                                    src={color.image}
                                  />
                                </ColorPicker>
                                <UncontrolledTooltip
                                  target={"internal_" + index}
                                  placement="bottom"
                                >
                                  {color.name}
                                </UncontrolledTooltip>
                              </>
                              <p className="m-0">{color.short_name}</p>
                              {access.change_color && (
                                <div className="d-flex justify-content-between align-items-center">
                                  <>
                                    <i
                                      id={"internal_update_" + index}
                                      className="fa-regular fa-pen-to-square icon-hover pointer"
                                      onClick={() => {
                                        setShowSearchColors(true);
                                        setLaminateType("internal");
                                        setActionType("update");
                                        setSelectedColor(color);
                                      }}
                                    ></i>
                                    <UncontrolledTooltip
                                      target={"internal_update_" + index}
                                      placement="bottom"
                                    >
                                      Change Color
                                    </UncontrolledTooltip>
                                  </>
                                  <>
                                    <i
                                      id={"internal_delete_" + index}
                                      className={`fa-regular fa-trash-can icon-hover ${
                                        !color.unused_color
                                          ? "grey_letter not-allowed-cursor"
                                          : "pointer"
                                      }`}
                                      onClick={() => {
                                        if (color.unused_color) {
                                          setDeleteOption(
                                            color.prop_identifier
                                          );
                                          toggleRemoveColorConfirmation();
                                        }
                                      }}
                                    ></i>
                                    <UncontrolledTooltip
                                      target={"internal_delete_" + index}
                                      placement="bottom"
                                    >
                                      {color.unused_color
                                        ? "Delete Color"
                                        : "Color in use cannot be deleted"}
                                    </UncontrolledTooltip>
                                  </>
                                </div>
                              )}
                            </div>
                          ))}
                        {access.add_color &&
                          colors &&
                          colors.internal.length <= count["internal"] && (
                            <div className="d-flex flex-column align-items-center">
                              <ColorPicker
                                className="d-flex justify-content-center align-items-center pointer"
                                style={{ background: "#D9D9D9" }}
                                onClick={() => {
                                  setShowSearchColors(true);
                                  setLaminateType("internal");
                                  setActionType("add");
                                }}
                              >
                                <i className="fa-solid fa-plus fs-5 text-secondary" />
                              </ColorPicker>
                              <span className="fs-10px text-secondary">
                                Add Color
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                    <div className="px-3">
                      <p className="mb-2 fw-bold fs-14px">External</p>
                      <div className="d-flex flex-wrap column-gap-4 row-gap-2">
                        {colors &&
                          colors.external.map((color, index) => (
                            <div
                              className="d-flex flex-column align-items-center"
                              key={index}
                            >
                              <>
                                <ColorPicker id={"external" + index}>
                                  {color.unused_color && (
                                    <div className="unused_color_text">
                                      Unused
                                    </div>
                                  )}
                                  <img
                                    className="h-100 w-100"
                                    src={color.image}
                                  />
                                </ColorPicker>
                                <UncontrolledTooltip
                                  target={"external" + index}
                                  placement="bottom"
                                >
                                  {color.name}
                                </UncontrolledTooltip>
                              </>
                              <p className="m-0">{color.short_name}</p>
                              {access.change_color && (
                                <div className="d-flex justify-content-between align-items-center">
                                  <>
                                    <i
                                      id={"external_update_" + index}
                                      className="fa-regular fa-pen-to-square icon-hover pointer"
                                      onClick={() => {
                                        setShowSearchColors(true);
                                        setLaminateType("external");
                                        setActionType("update");
                                        setSelectedColor(color);
                                      }}
                                    ></i>
                                    <UncontrolledTooltip
                                      target={"external_update_" + index}
                                      placement="bottom"
                                    >
                                      Change Color
                                    </UncontrolledTooltip>
                                  </>
                                  <>
                                    <i
                                      id={"external_delete_" + index}
                                      className={`fa-regular fa-trash-can icon-hover ${
                                        !color.unused_color
                                          ? "grey_letter not-allowed-cursor"
                                          : "pointer"
                                      }`}
                                      onClick={() => {
                                        if (color.unused_color) {
                                          setDeleteOption(
                                            color.prop_identifier
                                          );
                                          toggleRemoveColorConfirmation();
                                        }
                                      }}
                                    ></i>
                                    <UncontrolledTooltip
                                      target={"external_delete_" + index}
                                      placement="bottom"
                                    >
                                      {color.unused_color
                                        ? "Delete Color"
                                        : "Color in use cannot be deleted"}
                                    </UncontrolledTooltip>
                                  </>
                                </div>
                              )}
                            </div>
                          ))}
                        {access.add_color &&
                          colors &&
                          colors.external.length <= count["external"] && (
                            <div className="d-flex flex-column align-items-center">
                              <ColorPicker
                                className="d-flex justify-content-center align-items-center pointer"
                                style={{ background: "#D9D9D9" }}
                                onClick={() => {
                                  setShowSearchColors(true);
                                  setLaminateType("external");
                                  setActionType("add");
                                }}
                              >
                                <i className="fa-solid fa-plus fs-5 text-secondary" />
                              </ColorPicker>
                              <span className="fs-10px text-secondary">
                                Add Color
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                  </>
                )}
                {selectedTab === "edges" && (
                  <>
                    <div className="p-3 overflow-scroll mb-8 fh">
                      <div className="d-flex flex-wrap column-gap-4 row-gap-3">
                        {edgeTapeMapping &&
                          edgeTapeMapping.map((mapping, index) => (
                            <>
                              <div className={"flex w-100"}>
                                <div
                                  id={"icon_" + index}
                                  className={`d-flex flex-column align-items-center`}
                                  onClick={() => {}}
                                  key={index}
                                >
                                  <ColorIcon className={`disabled `}>
                                    <img
                                      className="h-100 w-100"
                                      src={mapping.laminate.image}
                                    />
                                  </ColorIcon>
                                  <p className="m-0">
                                    {mapping.laminate.short_name}
                                  </p>
                                </div>
                                <UncontrolledTooltip
                                  target={"icon_" + index}
                                  placement="bottom"
                                >
                                  {mapping.laminate.name}
                                </UncontrolledTooltip>

                                <div className={"ms-4 flex-grow-1 me-4"}>
                                  <span
                                    className={"text-secondary fs-12px"}
                                  >{`${mapping.panel_thickness}mm Panel / ${mapping.thickness}mm Edge`}</span>

                                  <Select
                                    value={getEdgeMaterialOption(
                                      mapping.edge_material_identifier
                                    )}
                                    className="mt-1"
                                    styles={colourStyles}
                                    isDisabled={!access.manage_edges}
                                    options={edgeMaterials.map(
                                      (edgeMaterialOptionObj) => ({
                                        name: edgeMaterialOptionObj.name,
                                        label: edgeMaterialOptionObj.name,
                                        value: edgeMaterialOptionObj.identifier,
                                        thickness:
                                          edgeMaterialOptionObj.thickness,
                                        width: edgeMaterialOptionObj.width,
                                      })
                                    )}
                                    onChange={(selectedEdgeMaterial) => {
                                      handleEdgeMaterialChange({
                                        edgeCategory: mapping,
                                        materialIdentifier:
                                          selectedEdgeMaterial.value,
                                      });
                                    }}
                                    placeholder={"Select edge"}
                                  />
                                </div>
                              </div>
                            </>
                          ))}
                      </div>
                      {access.manage_edges && (
                        <div
                          className="position-fixed bottom-0 d-flex align-items-center px-2 py-3 border-top border-secondary-subtle"
                          style={{
                            background: "#dddddd",
                            right: 0,
                            width: "300px",
                          }}
                        >
                          <p className="mb-0 me-1">
                            Could not find the Edge Tape?
                          </p>
                          <UploadButton
                            onClick={() => setShowUploadEdgeTape(true)}
                          >
                            <i className="fa-solid fa-circle-plus me-1"></i>
                            Add
                          </UploadButton>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>

      <ConfirmationModal
        isOpen={showRemoveColorConfirmation}
        toggle={toggleRemoveColorConfirmation}
        onConfirm={() => deleteColorEntry(deleteOption)}
        message="Are you sure you want to remove the color?"
        headingText="Remove Color"
      />
    </>
  );
};

export default Colors;
